import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Clock, CreditCard, Zap, Check } from 'lucide-react';
type PlanId = 'esencial' | 'pro';
const plans: {
  id: PlanId;
  name: string;
  subtitle: string;
  oldPrice: string;
  price: string;
  checkoutUrl: string;
  badge?: string;
  note?: string;
}[] = [{
  id: 'esencial',
  name: 'MEGAPACK MUEBLES JAPANDI — ESENCIAL',
  subtitle: '6 tomos + 5 bonos estratégicos',
  oldPrice: '$34.990',
  price: '$19.990',
  checkoutUrl: 'https://checkout.infinitosaber.online/cart/46363683389612:1'
}, {
  id: 'pro',
  name: 'MEGAPACK MUEBLES JAPANDI — PRO',
  subtitle: '6 tomos + 5 bonos + Tomo 7 + Tomo 8',
  oldPrice: '$43.990',
  price: '$29.990',
  checkoutUrl: 'https://checkout.infinitosaber.online/cart/46363685060780:1',
  badge: '⭐ MÁS ELEGIDO · AHORRÁS $10.000',
  note: 'La opción más elegida por fabricantes que quieren convertirlo en negocio real'
}];
export function Pricing() {
  const [selected, setSelected] = useState<PlanId>('pro');
  const selectedPlan = plans.find(p => p.id === selected)!;
  return <section id="pricing" className="py-8 md:py-16 px-4 bg-[#F5F0E8]" data-id="element-183">
      <div className="max-w-3xl mx-auto" data-id="element-184">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-2xl md:text-5xl font-bold text-center mb-2 md:mb-4" data-id="element-185">
          Elegí tu Megapack Muebles Japandi
        </motion.h2>

        <p className="text-center text-sm md:text-lg mb-6 md:mb-8 text-[#2B2724]/80" data-id="element-186">
          Tocá una opción para elegirla — ambas incluyen los 5 bonos completos
        </p>

        <div className="flex flex-col gap-3 md:gap-4 mb-6" data-id="element-187">
          {plans.map(plan => {
          const isSelected = selected === plan.id;
          const isPro = plan.id === 'pro';
          return <motion.button key={plan.id} type="button" onClick={() => setSelected(plan.id)} whileTap={{
            scale: 0.99
          }} aria-pressed={isSelected} className={`relative w-full text-left rounded-xl border-2 p-4 md:p-5 transition-all ${isSelected ? 'border-[#C2613A] bg-white shadow-lg ring-2 ring-[#C2613A]/30' : 'border-[#2B2724]/10 bg-white/60 hover:border-[#2B2724]/25'}`} data-id="element-188">
                <div className="flex items-center gap-3 md:gap-4" data-id="element-189">
                  {/* Radio indicator */}
                  <span className={`flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full border-2 transition-colors ${isSelected ? 'border-[#C2613A] bg-[#C2613A]' : 'border-[#2B2724]/30 bg-transparent'}`} data-id="element-190">
                    {isSelected && <Check className="w-4 h-4 text-white" data-id="element-191" />}
                  </span>

                  {/* Info */}
                  <div className="flex-1 min-w-0" data-id="element-192">
                    {plan.badge && <span className="inline-block bg-[#C2613A] text-white text-[9px] md:text-[11px] px-2 py-0.5 rounded-full font-bold pt-[0px] pb-[0px] pl-[0px] pr-[0px] mt-[0px] mb-[0px] ml-[0px] mr-[0px]" data-id="element-193">
                        {plan.badge}
                      </span>}
                    <h3 className="font-bold text-sm md:text-lg leading-tight" data-id="element-194">
                      {plan.name}
                    </h3>
                    <p className="text-[11px] md:text-sm text-[#2B2724]/70" data-id="element-195">
                      {plan.subtitle}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="flex-shrink-0 text-right" data-id="element-196">
                    <span className="block text-[11px] md:text-sm text-[#2B2724]/40 line-through leading-none" data-id="element-197">
                      {plan.oldPrice}
                    </span>
                    <span className={`block text-2xl md:text-4xl font-bold leading-tight ${isPro ? 'text-[#C2613A]' : 'text-[#2B2724]'}`} data-id="element-198">
                      {plan.price}
                    </span>
                  </div>
                </div>
              </motion.button>;
        })}
        </div>

        {/* Dynamic CTA based on selection */}
        <motion.a href={selectedPlan.checkoutUrl} onClick={() => {
        if (typeof window !== 'undefined' && window.fbq) {
          window.fbq('track', 'InitiateCheckout', {
            content_name: selectedPlan.name,
            currency: 'ARS'
          });
        }
      }} whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.98
      }} className="block w-full bg-[#C2613A] hover:bg-[#A84F2E] text-white text-center py-4 rounded-lg text-base md:text-lg font-bold transition-colors mb-2" data-id="element-199">
          {selected === 'pro' ? 'SÍ, QUIERO EL MEGAPACK PRO' : 'QUIERO EL MEGAPACK ESENCIAL'}{' '}
          · {selectedPlan.price}
        </motion.a>

        {selectedPlan.note && <p className="text-[11px] md:text-xs text-[#2B2724]/60 text-center mb-6 md:mb-8" data-id="element-200">
            {selectedPlan.note}
          </p>}

        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="flex flex-wrap justify-center gap-3 md:gap-6 text-[10px] md:text-sm mt-4" data-id="element-201">
          <div className="flex items-center gap-1 md:gap-2" data-id="element-202">
            <ShieldCheck className="w-4 h-4 md:w-5 md:h-5 text-[#C2613A]" data-id="element-203" />
            <span data-id="element-204">Pago seguro</span>
          </div>
          <div className="flex items-center gap-1 md:gap-2" data-id="element-205">
            <Clock className="w-4 h-4 md:w-5 md:h-5 text-[#C2613A]" data-id="element-206" />
            <span data-id="element-207">Acceso en 5 min</span>
          </div>
          <div className="flex items-center gap-1 md:gap-2" data-id="element-208">
            <Zap className="w-4 h-4 md:w-5 md:h-5 text-[#C2613A]" data-id="element-209" />
            <span data-id="element-210">Garantía 7 días</span>
          </div>
          <div className="flex items-center gap-1 md:gap-2" data-id="element-211">
            <CreditCard className="w-4 h-4 md:w-5 md:h-5 text-[#C2613A]" data-id="element-212" />
            <span data-id="element-213">Mercado Pago</span>
          </div>
        </motion.div>
      </div>
    </section>;
}